import { yupResolver } from '@hookform/resolvers/yup'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Link, useParams } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import { object, string } from 'yup'
import {
    AccountRecord,
    CreateMonitoredAccountMutation,
    useCreateMonitoredAccountMutation
} from '../../api/client'
import Button from '../../components/Button'
import { toastError, toastSuccess } from '../../components/Notification'
import SimpleLoader from '../../components/SimpleLoader'
import { ComboboxMenuOption } from '../../components/inputs/ComboboxMenuOption'
import TeamSelectMenu from '../../components/team/TeamSelectMenu'
import { TextPageContainer } from '../../containers/TextPageContainer'
import PageMasthead from '../../components/PageMasthead'
import { shouldRefetchAccountsState } from '../../store/UIStore'
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'

/**
 * A monitored account is an account we have no authorisation for but whose
 * comments we collect via scrapes — e.g. creator accounts covered by a client
 * campaign. This page lets admins create one so that first line can then set
 * up keywords and moderation boundaries before scraping is configured.
 */
type PlatformConfig = {
    title: string
    /** The serviceName string the Rust mutation parses (strum lowercase form). */
    serviceName: string
    usernameLabel: string
    usernameHint: string
    needsChannelId: boolean
    /** What has to happen after creation before anything is collected, if a person must do it. */
    afterCreateHint?: string
}

const platforms: Record<string, PlatformConfig> = {
    instagram: {
        title: 'Instagram profile',
        serviceName: 'instagram',
        usernameLabel: 'Instagram username',
        usernameHint: 'The exact profile name, e.g. hotelsdotcom from instagram.com/hotelsdotcom',
        needsChannelId: false
    },
    facebook: {
        title: 'Facebook page',
        serviceName: 'facebook',
        usernameLabel: 'Facebook page name',
        usernameHint:
            'The exact page name, e.g. Hotels.comEurope from facebook.com/Hotels.comEurope',
        needsChannelId: false
    },
    tiktok_user: {
        title: 'TikTok user account',
        serviceName: 'tiktokuser',
        usernameLabel: 'TikTok username',
        usernameHint: 'The exact handle without the @, e.g. nwslsoccer from tiktok.com/@nwslsoccer',
        needsChannelId: false
    },
    youtube: {
        title: 'YouTube channel',
        serviceName: 'youtube',
        usernameLabel: 'Channel handle',
        usernameHint: 'The handle without the @, e.g. nwsl from youtube.com/@nwsl',
        needsChannelId: true,
        // Not switched on at creation: a person decides when a channel starts
        // spending the shared YouTube quota.
        afterCreateHint:
            "Collection starts once an admin switches Fetch with API on in the channel's settings. Arwen then polls the channel for uploads every 30 minutes and collects their comments on its own API key."
    }
}

interface FormData {
    username: string
    accountName?: string
    channelId?: string
}

function validationSchema(platform: PlatformConfig) {
    return object({
        username: string().trim().required(`${platform.usernameLabel} is required`),
        accountName: string(),
        channelId: platform.needsChannelId
            ? string()
                  .trim()
                  .required('Channel ID is required — content fetching is keyed on it')
                  .min(4, 'Channel ID must be at least 4 characters')
            : string()
    })
}

function NewMonitoredAccountPage() {
    const { platform: platformParam } = useParams()
    const platform = platformParam ? platforms[platformParam] : undefined

    const [account, setAccount] = useState<AccountRecord | null>()
    const [selectedTeams, setSelectedTeams] = useState<ComboboxMenuOption[]>([])

    /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
    const [_shouldRefetchAccounts, setShouldRefetchAccounts] = useRecoilState(
        shouldRefetchAccountsState
    )

    const form = useForm<FormData>({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(validationSchema(platform ?? platforms.instagram)),
        defaultValues: { username: '', accountName: '', channelId: '' }
    })

    const [createMonitoredAccountMutation, { loading }] = useCreateMonitoredAccountMutation({
        onCompleted: (data: CreateMonitoredAccountMutation) => {
            if (data.createMonitoredAccount) {
                setAccount(data.createMonitoredAccount)
                setShouldRefetchAccounts(true)
                toastSuccess(`Monitored account ${data.createMonitoredAccount.accountName} created`)
            } else {
                console.error(
                    'We created a monitored account but did not get an account back from the server!'
                )
            }
        },
        onError: (error) => {
            toastError(error.message)
        }
    })

    if (!platform) {
        return (
            <>
                <PageMasthead title="Unknown platform" tabTitle="Unknown platform" />
                <TextPageContainer>
                    <p>
                        Monitoring is available for Instagram, Facebook, TikTok user accounts and
                        YouTube channels.
                    </p>
                </TextPageContainer>
            </>
        )
    }

    if (account) {
        return (
            <>
                <PageMasthead
                    title="Monitored account created -- action required"
                    tabTitle="Monitored account created"
                />
                <TextPageContainer>
                    <p>
                        <strong>{account.accountName}</strong> has been created but there are two
                        steps to get it monitored:
                    </p>
                    <ol className="list-decimal list-inside flex flex-col gap-2">
                        <li>
                            Configure the account in{' '}
                            <Link
                                to={`/account/${account.id}/settings`}
                                className="text-primary-600 underline hover:text-primary-800"
                            >
                                account settings
                            </Link>{' '}
                            — teams, keywords and moderation boundaries.
                        </li>
                        <li>
                            <a
                                data-tooltip-id="tooltip-id"
                                data-tooltip-content="Go to Jira to raise a support ticket"
                                href="https://arwen-ai.atlassian.net/jira/servicedesk/projects/SUP"
                                target="_blank"
                                className="text-primary-600 hover:text-primary-800"
                                rel="noreferrer"
                            >
                                {/* text-decoration doesn't propagate into flex boxes, so
                                    underline the text directly and keep the icon inline */}
                                <span className="underline">Raise a ticket with support</span>
                                <ArrowTopRightOnSquareIcon className="ml-1 w-4 inline align-text-bottom" />
                            </a>{' '}
                            to add the profile to monitoring.
                        </li>
                    </ol>
                </TextPageContainer>
            </>
        )
    }

    // The mutation needs exactly one team; the shared menu is multi-select, so
    // keep only the most recent choice.
    const selectTeam = (teams: ComboboxMenuOption[]) => setSelectedTeams(teams.slice(-1))
    const team = selectedTeams[0]

    const onSubmit = async (data: FormData) => {
        if (!team) {
            toastError('Please select a team')
            return
        }
        createMonitoredAccountMutation({
            variables: {
                serviceAccountUsername: data.username.trim(),
                serviceName: platform.serviceName,
                teamId: team.id,
                accountName: data.accountName?.trim() || undefined,
                youtubeChannelId: platform.needsChannelId ? data.channelId?.trim() : undefined
            }
        })
    }

    return (
        <>
            <PageMasthead title={platform.title} tabTitle={platform.title} />
            <TextPageContainer>
                <FormProvider {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                        <p>
                            Creates an account without authorisation. Comments arrive via scraping
                            only, so Arwen can analyse but not act on them.
                        </p>

                        <div className="mt-6 sm:mt-0 flex flex-col gap-4">
                            <div>
                                <label className="block" htmlFor="username">
                                    {platform.usernameLabel}
                                </label>
                                <p className="text-sm text-gray-500 mt-0 mb-1">
                                    {platform.usernameHint}
                                </p>
                                <input
                                    id="username"
                                    type="text"
                                    className="w-full rounded-md border-0 py-1.5 pl-3 text-gray-950 shadow ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                                    {...form.register('username')}
                                />
                                <p className="text-sm text-red-600">
                                    {form.formState.errors.username?.message}
                                </p>
                            </div>

                            {platform.needsChannelId ? (
                                <div>
                                    <label className="block" htmlFor="channelId">
                                        Channel ID
                                    </label>
                                    <p className="text-sm text-gray-500 mt-0 mb-1">
                                        Starts with UC — from the channel&apos;s About page or
                                        youtube.com/account_advanced
                                    </p>
                                    <input
                                        id="channelId"
                                        type="text"
                                        className="w-full rounded-md border-0 py-1.5 pl-3 text-gray-950 shadow ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                                        {...form.register('channelId')}
                                    />
                                    <p className="text-sm text-red-600">
                                        {form.formState.errors.channelId?.message}
                                    </p>
                                </div>
                            ) : null}

                            {platform.afterCreateHint ? (
                                <p className="text-sm text-gray-500">{platform.afterCreateHint}</p>
                            ) : null}

                            <div>
                                <label className="block" htmlFor="accountName">
                                    Display name (optional)
                                </label>
                                <p className="text-sm text-gray-500 mt-0 mb-1">
                                    Shown in Arwen — defaults to the username
                                </p>
                                <input
                                    id="accountName"
                                    type="text"
                                    className="w-full rounded-md border-0 py-1.5 pl-3 text-gray-950 shadow ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                                    {...form.register('accountName')}
                                />
                            </div>

                            <TeamSelectMenu
                                selectedTeams={selectedTeams}
                                setSelectedTeams={selectTeam}
                            />

                            <div className="mt-2">
                                <Button
                                    disabled={
                                        !form.formState.isValid ||
                                        form.formState.isSubmitting ||
                                        !team
                                    }
                                    primary={true}
                                    text="Create monitored account"
                                    type="submit"
                                    size="large"
                                />
                            </div>
                        </div>
                    </form>
                </FormProvider>
                <SimpleLoader loading={loading} />
            </TextPageContainer>
        </>
    )
}

export default NewMonitoredAccountPage
